import type { ApolloError, WatchQueryFetchPolicy } from "@apollo/client";
import { useQuery } from "@apollo/client";
import { useMemo } from "react";

// TODO(workOrder-refactor): replace with a dedicated GET_WORK_ORDER_DETAIL
// query that selects only the fields the new detail page actually renders.
// We currently reuse the existing single-ticket document so the new feature
// works end-to-end while we land the structural refactor.
import { GET_OWN_OEM_TICKET_BY_ID } from "~/api";

import { toWorkOrder } from "./transform";
import type { Id, WorkOrder } from "./types";

interface UseGetWorkOrderByIdResult {
  workOrder: WorkOrder | null;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => Promise<unknown>;
}

/**
 * Detail-page query for a single work order.
 *
 * Replaces the legacy `getTicketById` for callers in
 * `features/WorkOrderDetails`. Returns the domain shape `WorkOrder`,
 * not the raw GraphQL `Ticket`.
 */
export const useGetWorkOrderById = (
  id: Id | undefined,
  skipCondition = false,
  fetchPolicy: WatchQueryFetchPolicy = "cache-first",
): UseGetWorkOrderByIdResult => {
  const { data, loading, error, refetch } = useQuery(GET_OWN_OEM_TICKET_BY_ID, {
    variables: { id },
    skip: !id || skipCondition,
    // Page owner fetches `network-only` for freshness; every other consumer
    // reads `cache-first`, so they render instantly from the populated cache
    // instead of firing their own request (which caused a blank flash after
    // the page skeleton and before each section's data arrived).
    fetchPolicy,
  });

  // Memoize so callers that depend on the returned object reference (e.g.
  // useEffect deps) don't re-run on every render. Apollo stabilizes `data`
  // through cache normalization; we just need to avoid creating a new object
  // on each render when the underlying data hasn't changed.
  const workOrder = useMemo(
    () => toWorkOrder(data?.getOwnOemTicketById),
    [data],
  );

  return { workOrder, loading, error, refetch };
};
